import type { CatalogRule, Filter } from '@repo/api-codegen';
import type {
	AddedFilterResult,
	FILTER_OPTIONS_DIVIDER,
	FilterOption,
	FilterValue,
	TopLevelOperatorType,
} from '@repo/common/components/Filter';
import {
	FilterOptionType,
	getApiCatalogFilterFromFilterValues,
	getFilterValueFromApiCatalogFilter,
} from '@repo/common/components/Filter';
import { isEmpty } from 'lodash-es';
import { makeAutoObservable } from 'mobx';

function mergeQualityFlagFilters(filtersValues: FilterValue[]): FilterValue[] {
	const qualityFlagFilters = filtersValues.filter(
		(filterValue) => filterValue.filterType === FilterOptionType.QUALITY_FLAGS
	);

	if (qualityFlagFilters.length <= 1) {
		return filtersValues;
	}

	const baseFilter = qualityFlagFilters[0];

	const shareSameOperator = qualityFlagFilters.every(
		(filter) =>
			filter.operator === baseFilter.operator &&
			filter.isNotSetApplied === baseFilter.isNotSetApplied &&
			filter.isSetApplied === baseFilter.isSetApplied
	);

	if (!shareSameOperator) {
		return filtersValues;
	}

	const combinedValue = Array.from(
		new Set(
			qualityFlagFilters.flatMap((filter) => {
				if (Array.isArray(filter.value)) {
					return filter.value.filter(
						(value): value is string => typeof value === 'string'
					);
				}

				return typeof filter.value === 'string' ? [filter.value] : [];
			})
		)
	);

	const otherFilters = filtersValues.filter(
		(filterValue) => filterValue.filterType !== FilterOptionType.QUALITY_FLAGS
	);

	if (combinedValue.length === 0) {
		return otherFilters;
	}

	return [
		...otherFilters,
		{
			...baseFilter,
			value: combinedValue,
		},
	];
}

export class FilterRule {
	filtersValues: Array<FilterValue>;
	operator: TopLevelOperatorType;
	store: FilterBlockStore;

	constructor(
		store: FilterBlockStore,
		filtersValues: Array<FilterValue>,
		operator: TopLevelOperatorType
	) {
		makeAutoObservable(this, {}, { autoBind: true });

		this.store = store;
		this.filtersValues = filtersValues;
		this.operator = operator;
	}

	setOperator(operator: TopLevelOperatorType) {
		this.operator = operator;
		this.store.refreshCachedCatalogFilter();
	}

	onAddValue(value: FilterValue) {
		this.filtersValues.push(value);
		this.store.refreshCachedCatalogFilter();

		return {
			value,
			changeFilter: this.onChangeValue(this.filtersValues.length - 1),
			clearFilter: this.onClearValue(this.filtersValues.length - 1),
		} as AddedFilterResult;
	}

	onChangeValue(index: number) {
		return (value: Partial<FilterValue>) => {
			this.filtersValues[index] = {
				...this.filtersValues[index],
				...value,
			};
			this.store.refreshCachedCatalogFilter();
		};
	}

	onClearValue(index: number) {
		return () => {
			this.filtersValues.splice(index, 1);
			this.store.refreshCachedCatalogFilter();
		};
	}
}

export class FilterBlockStore {
	filterOptions: Array<FilterOption | typeof FILTER_OPTIONS_DIVIDER>;
	filterRules: Array<FilterRule> = [];
	topLevelOperator: TopLevelOperatorType = 'and';
	includeChildFilters: boolean = false;

	filtersLoading: boolean = true;
	catalogFilter: CatalogRule | undefined;

	constructor(
		filterOptions: Array<FilterOption | typeof FILTER_OPTIONS_DIVIDER>,
		initialRule: CatalogRule,
		includeEmptyRule: boolean
	) {
		makeAutoObservable(this, {}, { autoBind: true });

		this.filterOptions = filterOptions;

		const self = this;

		async function parseFilters() {
			const filterValues = await Promise.all(
				initialRule.operands.map(async (operand) => {
					const filtersValues = await getFilterValueFromApiCatalogFilter(
						filterOptions,
						operand,
						false,
						true
					);
					const normalizedFiltersValues =
						mergeQualityFlagFilters(filtersValues);
					return new FilterRule(
						self,
						normalizedFiltersValues.map((filterValue) => ({
							...filterValue,
							value: Array.isArray(filterValue.value)
								? [...new Set(filterValue.value)]
								: filterValue.value,
						})),
						operand.operator as TopLevelOperatorType
					);
				})
			);

			self.filterRules = filterValues;
			self.topLevelOperator = initialRule.operator;
			if (includeEmptyRule && isEmpty(initialRule.operands)) {
				self.filterRules.push(new FilterRule(self, [], 'and'));
			}
			self.refreshCachedCatalogFilter().then(() => {
				self.filtersLoading = false;
			});
		}

		parseFilters();
	}

	setTopLevelOperator(operator: TopLevelOperatorType) {
		this.topLevelOperator = operator;
		this.refreshCachedCatalogFilter();
	}

	addNewRule() {
		this.filterRules.push(new FilterRule(this, [], 'and'));
		this.refreshCachedCatalogFilter();
	}

	removeRule(index: number) {
		this.filterRules.splice(index, 1);
		this.refreshCachedCatalogFilter();
	}

	refreshCachedCatalogFilter = async () => {
		const allValues = await Promise.all(
			this.filterRules.map((rule) =>
				getApiCatalogFilterFromFilterValues(
					this.filterOptions,
					rule.filtersValues,
					rule.operator
				)
			)
		);

		this.catalogFilter = {
			operator: this.topLevelOperator,
			operands: allValues.filter((value) => value),
		} as CatalogRule;

		this.setIncludeChildFilters(allValues);
	};

	private setIncludeChildFilters(allValues: (Filter | undefined)[]) {
		// Recursive function to check if any filter contains child filter types
		const hasChildFilters = (filter?: Filter): boolean => {
			if (!filter) {
				return false;
			}

			// Check if the current filter field is a child filter type
			if (
				filter.field &&
				['any_child', 'all_children', 'no_children'].includes(filter.field)
			) {
				return true;
			}

			// Recursively check all operands
			if (filter.operands && filter.operands.length > 0) {
				for (const operand of filter.operands) {
					if (hasChildFilters(operand)) {
						return true;
					}
				}
			}

			return false;
		};

		this.includeChildFilters = allValues.some(hasChildFilters);
	}
}
